import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar, Link as LinkIcon, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface GoogleCalendarAuthProps {
  onCalendarConnected: (accessToken: string) => void;
  isConnected: boolean;
  onDisconnect: () => void;
}

const GoogleCalendarAuth = ({ onCalendarConnected, isConnected, onDisconnect }: GoogleCalendarAuthProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Check if user is already authenticated with Google via Supabase
  useEffect(() => {
    checkGoogleAuth();
  }, []);

  const checkGoogleAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.provider_token && session?.user?.app_metadata?.provider === 'google') {
        // User is authenticated with Google through Supabase
        const userProfile = {
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
          picture: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture,
          email: session.user.email
        };
        
        // Keep the access token in memory only (passed up via callback to React state).
        // Persisting it to localStorage makes it exfiltratable via XSS.
        localStorage.setItem('google_user_profile', JSON.stringify(userProfile));
        onCalendarConnected(session.provider_token);
      }
    } catch (error) {
      console.error('Error checking Google auth:', error);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      // Since we're using Supabase OAuth, redirect to re-authenticate with calendar scope
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${window.location.pathname}`,
          scopes: 'https://www.googleapis.com/auth/calendar.readonly'
        }
      });

      if (error) throw error;

    } catch (error) {
      console.error('Google Calendar auth error:', error);
      toast({
        title: "Connection Failed", 
        description: "Failed to connect to Google Calendar. Please try again.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem('google_user_profile');
    onDisconnect();
    toast({
      title: "Disconnected",
      description: "Google Calendar has been disconnected.",
    });
  };

  if (isConnected) {
    const userProfile = localStorage.getItem('google_user_profile');
    const profile = userProfile ? JSON.parse(userProfile) : null;
    
    return (
      <div className="flex items-center justify-between mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-center gap-3">
          {profile?.picture && (
            <Avatar className="h-8 w-8">
              <AvatarImage src={profile.picture} alt={profile.name || "User"} />
              <AvatarFallback>
                {profile.name ? profile.name.charAt(0).toUpperCase() : "U"}
              </AvatarFallback>
            </Avatar>
          )}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-600" />
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Google Calendar Connected
              </Badge>
            </div>
            {profile?.name && (
              <p className="text-xs text-green-700 font-medium">{profile.name}</p>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleDisconnect}>
          Disconnect
        </Button>
      </div>
    );
  }

  return (
    <Card className="mb-4 border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <LinkIcon className="h-4 w-4" />
          Connect Google Calendar
        </CardTitle>
        <CardDescription className="text-xs">
          Connect your Google Calendar to view your personal events
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button 
          onClick={handleGoogleAuth} 
          disabled={loading}
          size="sm"
          className="w-full"
        >
          {loading ? (
            <>
              <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Calendar className="h-3 w-3 mr-2" />
              Connect Google Calendar
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default GoogleCalendarAuth;